import React, { useEffect, useState } from "react";
import axios from "axios";
import { getToken } from "utils/getToken";
import {
    emptyState,
    fieldLabel,
    input,
    microLabel,
    pageKicker,
    pageTitle,
    primaryBtn,
    select,
    table,
    tablePanel,
    td,
    th,
    trHover,
} from "components/ui/instrument";

// Function to format date from yyyy-mm-dd to dd/mm/yyyy
const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const BASE_URL = process.env.REACT_APP_API_URL
const NIGHT_DUTY_LIST_URL = BASE_URL + "doctors/night-duty/list/";
const CREATE_NIGHT_DUTY_URL = BASE_URL + "doctors/create-night-duty/";
const DOCTORS_LIST_URL = BASE_URL + "doctors/night-duty/doctors/list/";

// TODO: Replace with secure token management

const NightDoctorDuty = () => {
    const [nightDuties, setNightDuties] = useState<any[]>([]);
    const [doctors, setDoctors] = useState<any[]>([]);
    const [selectedDoctor, setSelectedDoctor] = useState("");
    const [date, setDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [token, setToken] = useState(getToken());

    useEffect(() => {
        fetchNightDuties();
        fetchDoctors();
    }, []);

    const fetchNightDuties = async () => {
        try {
            setLoading(true);
            const res = await axios.get(NIGHT_DUTY_LIST_URL, {
                headers: { Authorization: `Token ${token}` },
            });
            setNightDuties(res.data);
        } catch (err) {
            setMessage("Failed to fetch night duty entries.");
        } finally {
            setLoading(false);
        }
    };

    const fetchDoctors = async () => {
        try {
            const res = await axios.get(DOCTORS_LIST_URL, {
                headers: { Authorization: `Token ${token}` },
            });
            setDoctors(res.data);
        } catch (err) {
            setMessage("Failed to fetch doctors list.");
        }
    };

    const handleCreateNightDuty = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!selectedDoctor || !date) {
            setMessage("Please select a doctor and date.");
            return;
        }
        try {
            setLoading(true);
            setMessage("");
            const res = await axios.post(
                CREATE_NIGHT_DUTY_URL,
                { doctor: selectedDoctor, date },
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            setMessage(res.data.message || "Night duty created successfully.");
            setSelectedDoctor("");
            setDate("");
            fetchNightDuties();
        } catch (err: any) {
            setMessage(
                err.response?.data?.message || "Failed to create night duty."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <header className="mb-5">
                <p className={pageKicker}>Babynama Clinic-OS</p>
                <h2 className={pageTitle}>Night Doctor Duty</h2>
            </header>
            <div className="rounded-lg border border-clinic-200 bg-white p-6 shadow-sm dark:border-navy-700 dark:!bg-navy-800">
                <h3 className={`${microLabel} mb-3`}>Create Night Duty</h3>
                <form onSubmit={handleCreateNightDuty} className="space-y-4 mb-6">
                    <div>
                        <label className={fieldLabel}>Doctor</label>
                        <select
                            value={selectedDoctor}
                            onChange={(e) => setSelectedDoctor(e.target.value)}
                            className={`${select} w-full`}
                            required
                        >
                            <option value="">Select doctor</option>
                            {doctors.map((doc) => (
                                <option key={doc.id} value={doc.id}>
                                    {doc.name} {doc.internal_calling_number ? `(${doc.internal_calling_number})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={fieldLabel}>Date</label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className={`${input} tabular-nums`}
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`${primaryBtn} w-full`}
                    >
                        {loading ? "Creating..." : "Create Night Duty"}
                    </button>
                </form>
                {message && (
                    <div className={`mb-6 text-center text-sm font-medium ${message.includes("success") ? "text-clinic-700 dark:text-gray-200" : "text-crit-600"}`}>
                        {message}
                    </div>
                )}
                <h3 className={`${microLabel} mb-3 mt-6 block`}>Previous Night Duty Entries</h3>
                {loading && <div className="py-2 text-center text-sm text-clinic-500 dark:text-gray-400">Loading...</div>}
                {!loading && nightDuties.length === 0 && <div className={emptyState}>No entries found.</div>}
                {!loading && nightDuties.length > 0 && (
                    <div className={`${tablePanel} custom-scrollbar`}>
                        <table className={table}>
                            <thead>
                                <tr>
                                    <th className={th}>Doctor</th>
                                    <th className={th}>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {nightDuties.map((entry) => (
                                    <tr key={entry.id} className={trHover}>
                                        <td className={td}>{entry.doctor_name}</td>
                                        <td className={`${td} tabular-nums`}>{formatDate(entry.date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NightDoctorDuty;
